import { useState } from 'react';

import { ApiError, api } from '../../lib/api.js';
import type { EntityDesc } from '../../lib/scene-doc.js';
import { logEditor } from '../../state/console.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { Icon } from '../ui/icons.js';

interface BakeResult {
  baked: { source: string; image: string; views: number; tile: number }[];
  note?: string;
}

/** `impostor` settings of the entity's impostor levels (the bake request body for each). */
function impostorLevels(value: Record<string, unknown>): Record<string, unknown>[] {
  const levels = Array.isArray(value.levels) ? (value.levels as Record<string, unknown>[]) : [];
  return levels
    .filter((l) => l && typeof l === 'object' && l.source === 'impostor')
    .map((l) => (l.impostor && typeof l.impostor === 'object' ? (l.impostor as Record<string, unknown>) : {}));
}

/** Runs one bake request on the server (`prism model impostor`). */
export async function bakeImpostors(body: Record<string, unknown> = {}): Promise<BakeResult | null> {
  const projectId = useWorkspace.getState().projectId;
  if (!projectId) return null;
  try {
    const result = await api.post<BakeResult>(`/projects/${projectId}/impostors/bake`, body);
    for (const b of result.baked) logEditor('info', `Baked impostor ${b.image} (${b.views} views, ${b.tile}px) for ${b.source}.`);
    if (result.note) logEditor('info', `Impostors: ${result.note}`);
    useProjectFiles.getState().refreshSoon();
    return result;
  } catch (error) {
    logEditor('error', `Impostor baking failed: ${error instanceof ApiError || error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}

/**
 * Inspector extra for the `lod` component: bakes the impostor atlases of this
 * entity's impostor levels (or of every impostor level in the project) with
 * the native renderer on the server. The browser runtime only loads baked
 * atlases; reopen the scene or press Play to see new ones.
 */
export function ImpostorBakeButton({ entity, value }: { entity: EntityDesc; value: Record<string, unknown> }) {
  const [busy, setBusy] = useState(false);
  const levels = impostorLevels(value);
  const mesh = entity.components?.mesh as { source?: unknown } | undefined;
  const source = typeof mesh?.source === 'string' ? mesh.source : null;

  const run = async (all: boolean) => {
    setBusy(true);
    try {
      if (all || !source) {
        await bakeImpostors({});
      } else {
        for (const imp of levels) {
          const body: Record<string, unknown> = { source };
          for (const key of ['layout', 'frames', 'resolution', 'alphaCutoff'] as const) {
            if (imp[key] !== undefined) body[key] = imp[key];
          }
          await bakeImpostors(body);
        }
      }
      logEditor('info', 'Reopen the scene or press Play to load the new impostor atlases.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="add-row" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <button
        type="button"
        className="ghost small"
        disabled={busy || levels.length === 0 || !source}
        title={
          !source
            ? 'This entity has no mesh source to bake'
            : levels.length === 0
              ? 'No level uses "source": "impostor"'
              : 'Render this entity\'s impostor atlases into impostors/ (prism model impostor)'
        }
        onClick={() => void run(false)}
      >
        <Icon name="sparkles" size={13} /> {busy ? 'Baking…' : 'Bake impostors'}
      </button>
      <button
        type="button"
        className="ghost small"
        disabled={busy}
        title="Bake every impostor level used by the project's scenes and prefabs"
        onClick={() => void run(true)}
      >
        <Icon name="layers" size={13} /> Bake all in project
      </button>
    </div>
  );
}
